/**
 * Ресурс «Аномалии» модуля analytics (L2) — витрина ML-аналитики пар
 * импорт/экспорт (сервис transit-ai).
 *
 * МАРШРУТ (временный, через модуль ai): выделенного gateway-маршрута
 * «analytics» пока нет, поэтому витрины отдаёт transit-ai —
 * `/api/ai/v1/analytics/anomalies` → gateway (маршрут ai) → под transit-ai →
 * сервис аномалий на claude-host (transit-anomaly.service, логика 1:1 с
 * localhost:3001). Когда платформа заведёт маршрут analytics за gateway —
 * вернуть BASE на `/api/analytics/anomalies` (или buildModuleBase).
 */

import { http } from '@/lib/api/http'
import type {
  AnomalySummary,
  AnomalyListResponse,
  AnomalyDetail,
  RecommendationRow,
} from './anomalies.types'
import {
  isRecommendationsV2Response,
  type RecommendationsV2Response,
} from './recommendations-v2.types'

const BASE = '/api/ai/v1/analytics/anomalies'

// 401 от upstream-сервиса аномалий НЕ должен разлогинивать пользователя из
// платформы (сессии независимы) — поэтому skipAuthRedirect на всех запросах.
const OPTS = { skipAuthRedirect: true, cache: 'no-store' as RequestCache }

export interface AnomalyFilterParams {
  exclude_eaeu?: boolean
  ml_level?: string
  hide_empty_route?: boolean
  ht_level?: string
  level?: string
  search?: string
  scenario_id?: number
}

export interface AnomalyListParams extends AnomalyFilterParams {
  scenario_profile?: string
  sort?: string
  limit?: number
  offset?: number
}

export function getAnomalySummary(filters: AnomalyFilterParams = {}): Promise<AnomalySummary> {
  return http.get<AnomalySummary>(BASE, { ...OPTS, params: { view: 'summary', ...filters } })
}

export function getAnomalyList(params: AnomalyListParams = {}): Promise<AnomalyListResponse> {
  return http.get<AnomalyListResponse>(BASE, { ...OPTS, params: { view: 'list', ...params } })
}

export function getAnomalyDetail(caseId: string): Promise<AnomalyDetail> {
  return http.get<AnomalyDetail>(`${BASE}/${encodeURIComponent(caseId)}`, OPTS)
}

/*
 * Легаси-выдача ГРУЗОВ (`view=cargo`, `view=cargo-cases`) здесь БОЛЬШЕ НЕ
 * ВЫЗЫВАЕТСЯ и её клиентские функции удалены.
 *
 * Причина не в чистоте кода. Легаси-слой отдаёт другую популяцию кейсов, другую
 * шкалу уровней и другой ML-трек: контрольный кейс `5_5562` приходит оттуда с
 * `ml_risk_score = 10`, тогда как канонический слой того же кейса даёт 5.563395.
 * Пока функция существует, она остаётся готовым «запасным путём», к которому
 * потянется первая же обработка ошибки, — и вкладка молча покажет устаревшие
 * числа под текущими подписями. Убран сам инструмент, а не только его вызов.
 *
 * Эндпоинты `:8002` при этом живы и совместимость не нарушена — они просто
 * больше не имеют потребителя во фронтенде. Текущий источник вкладки «Грузы» —
 * `cargo-current.api.ts`.
 */

export function getRecommendations(filters: AnomalyFilterParams = {}): Promise<{ rows: RecommendationRow[] }> {
  return http.get<{ rows: RecommendationRow[] }>(BASE, {
    ...OPTS,
    params: { view: 'recommendations', ...filters },
  })
}

/**
 * Recommendations V2 — сертифицированный контракт `view=recommendations-v2`.
 *
 * Ходит по той же цепочке и тем же helper'ом, что легаси: :3000 → :3001 →
 * :8005 → :8002. Абсолютных адресов в исходнике нет — путь окружения-независим,
 * как и у остальных запросов аналитики.
 *
 * Форма ответа проверяется здесь, а не в компоненте: сломанный контракт должен
 * приводить к запасному легаси-пути, а не к наполовину отрисованной вкладке.
 * Пустой `rows` поломкой НЕ считается — это валидное состояние.
 */
export async function getRecommendationsV2(
  filters: AnomalyFilterParams = {},
): Promise<RecommendationsV2Response> {
  const raw = await http.get<unknown>(BASE, {
    ...OPTS,
    params: { view: 'recommendations-v2', ...filters },
  })
  if (!isRecommendationsV2Response(raw)) {
    throw new RecommendationsV2ContractError('Ответ recommendations-v2 не соответствует контракту')
  }
  return raw
}

/**
 * Отдельный тип ошибки: он отличает «контракт сломан» от «сеть/HTTP упали».
 * Оба случая ведут на легаси, но в отчётах и тестах их полезно различать.
 */
export class RecommendationsV2ContractError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RecommendationsV2ContractError'
  }
}
